/**
 * AreaSelectionModal Component
 * Modal for selecting which tutorial areas to include
 */

import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Bot,
  Calendar,
  Settings,
  TrendingUp,
  Star,
  Users,
  Globe,
  Sparkles,
} from "lucide-react";
import { Dialog } from "../Dialog";
import Button from "../Button";
import { useTutorial } from "../../hooks/useTutorial";
import type { TutorialArea } from "./types";
import { getUser } from "../../helpers/utils";
import { useBookingEngineGoLive } from "../../helpers/hooks/use-booking-engine-settings";

interface AreaOption {
  id: TutorialArea;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  isAvailable: boolean;
}

export function AreaSelectionModal() {
  const { showAreaSelection, closeAreaSelection, startTutorial, startBriefTour } =
    useTutorial();
  const user = getUser();
  const userRole = user?.type || "U";
  // Same per-user runtime gate the sidebar uses for its Booking Engine item
  const { data: goLiveData } = useBookingEngineGoLive();
  const hasBookingEngine = !!(
    goLiveData?.showGoLiveButton || goLiveData?.shouldGoLive
  );

  // Determine which areas are available based on user role and, where the
  // sidebar gates per account (Booking Engine), the same runtime signal
  // Ordered to match sidebar navigation (Settings last)
  const areaOptions: AreaOption[] = [
    {
      id: "dashboard" as TutorialArea,
      label: "Main Dashboard",
      description:
        "Your command center for revenue, pricing, bookings, and occupancy metrics",
      icon: LayoutDashboard,
      isAvailable: userRole === "U" || userRole === "Admin",
    },
    {
      id: "ai-copilot" as TutorialArea,
      label: "AI CoPilot",
      description:
        "Your on-demand AI assistant for insights, data analysis, and taking actions",
      icon: Bot,
      isAvailable: userRole === "U" || userRole === "Admin",
    },
    {
      id: "calendar" as TutorialArea,
      label: "Calendar Management",
      description: "Override prices, adjust room-type differentials, and manage pricing in bulk",
      icon: Calendar,
      isAvailable: userRole === "U" || userRole === "Admin",
    },
    {
      id: "marketing" as TutorialArea,
      label: "Marketing Analytics",
      description:
        "Track your marketing funnel from impressions to bookings, plus website traffic and insights",
      icon: TrendingUp,
      isAvailable: userRole === "MarketingOnly" || userRole === "U" || userRole === "Admin",
    },
    {
      id: "reputation" as TutorialArea,
      label: "Reputation Management",
      description:
        "See all guest reviews from Google, Booking.com, and Expedia in one place",
      icon: Star,
      isAvailable: true,
    },
    {
      id: "groups" as TutorialArea,
      label: "Group Bookings",
      description:
        "Generate AI-powered group booking quotes with optimized pricing in seconds",
      icon: Users,
      isAvailable: userRole === "U" || userRole === "Admin",
    },
    {
      id: "booking-engine" as TutorialArea,
      label: "Booking Engine",
      description:
        "Take commission-free reservations directly on your own website",
      icon: Globe,
      isAvailable:
        (userRole === "U" || userRole === "Admin") && hasBookingEngine,
    },
    {
      id: "settings" as TutorialArea,
      label: "Settings",
      description:
        "Configure seasons, competitors, and AI instructions that guide pricing decisions",
      icon: Settings,
      isAvailable: userRole === "U" || userRole === "Admin",
    },
  ].filter((area) => area.isAvailable);

  const [selectedAreas, setSelectedAreas] = useState<TutorialArea[]>([]);

  // Reset selections to match available areas every time the modal opens
  useEffect(() => {
    if (showAreaSelection) {
      setSelectedAreas(areaOptions.map((area) => area.id));
    }
    // Intentionally re-seed selections only when the modal opens; areaOptions
    // is recomputed each render and must not retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAreaSelection]);

  const toggleArea = (areaId: TutorialArea) => {
    setSelectedAreas((prev) =>
      prev.includes(areaId)
        ? prev.filter((id) => id !== areaId)
        : [...prev, areaId]
    );
  };

  const selectAll = () => {
    setSelectedAreas(areaOptions.map((area) => area.id));
  };

  const deselectAll = () => {
    setSelectedAreas([]);
  };

  const handleStart = () => {
    if (selectedAreas.length > 0) {
      startTutorial(selectedAreas);
    }
  };

  if (!showAreaSelection) {
    return null;
  }

  return (
    <Dialog
      variant="center"
      size="lg"
      zIndexClassName="z-[90]"
      onClose={closeAreaSelection}
      closeAriaLabel="Close modal"
      title="Take a Tour"
      subtitle="Replay the quick welcome tour, or pick any area for a deeper walkthrough."
      footer={
        <div className="flex items-center justify-between gap-4">
          <p className="text-footnote text-content-tertiary">
            {selectedAreas.length} area{selectedAreas.length !== 1 ? "s" : ""}{" "}
            selected
          </p>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={closeAreaSelection}>
              Cancel
            </Button>
            <Button
              onClick={handleStart}
              disabled={selectedAreas.length === 0}
            >
              Start Tour
            </Button>
          </div>
        </div>
      }
    >
        {/* Replay the brief welcome tour */}
        <button
          onClick={startBriefTour}
          className="w-full flex items-center gap-3 p-4 mb-4 rounded-[var(--d-r-lg)] border border-[var(--d-accent-soft)] bg-[var(--d-accent-tint)] hover:border-[var(--d-accent)] transition-all text-left"
        >
          <div className="flex-shrink-0 p-2 rounded-[var(--d-r-md)] bg-[var(--d-accent-soft)] text-[var(--d-accent-deep)]">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-callout font-semibold text-content-primary mb-1">
              Replay the 2-minute welcome tour
            </h3>
            <p className="text-footnote text-content-secondary">
              The quick once-around of the essentials
            </p>
          </div>
        </button>

        {/* Quick actions */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={selectAll}
            className="text-callout text-[var(--d-accent)] hover:text-[var(--d-accent-deep)] font-medium transition-colors"
          >
            Select All
          </button>
          <span className="text-content-tertiary">•</span>
          <button
            onClick={deselectAll}
            className="text-callout text-content-secondary hover:text-content-primary transition-colors"
          >
            Deselect All
          </button>
        </div>

        {/* Area selection grid */}
        <div className="grid gap-3">
          {areaOptions.map((area) => {
            const Icon = area.icon;
            const isSelected = selectedAreas.includes(area.id);

            return (
              <button
                key={area.id}
                onClick={() => toggleArea(area.id)}
                className={`
                  flex items-start gap-4 p-4 rounded-[var(--d-r-lg)] border transition-all text-left
                  ${
                    isSelected
                      ? "border-[var(--d-accent)] bg-[var(--d-accent-tint)]"
                      : "border-[var(--d-rule)] hover:border-[var(--d-accent-soft)] hover:bg-[var(--d-tint-surface)]"
                  }
                `}
              >
                {/* Checkbox */}
                <div className="flex-shrink-0 mt-0.5">
                  <div
                    className={`
                      w-4 h-4 rounded-full border flex items-center justify-center transition-colors
                      ${
                        isSelected
                          ? "border-[var(--d-accent)] bg-[var(--d-accent)]"
                          : "border-[var(--d-rule)]"
                      }
                    `}
                  >
                    {isSelected && (
                      <svg
                        className="w-2.5 h-2.5 text-[var(--d-surface)]"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Icon */}
                <div
                  className={`
                  flex-shrink-0 p-2 rounded-[var(--d-r-md)] transition-colors
                  ${
                    isSelected
                      ? "bg-[var(--d-accent-soft)] text-[var(--d-accent-deep)]"
                      : "bg-[var(--d-tint-page)] text-content-tertiary"
                  }
                `}
                >
                  <Icon className="w-5 h-5" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-callout font-semibold text-content-primary mb-1">
                    {area.label}
                  </h3>
                  <p className="text-footnote text-content-secondary leading-relaxed">
                    {area.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
    </Dialog>
  );
}
